import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { axiosInstance } from '../../../helpers/api_helper';
import { WithdrawalStatusEnum, Withdrawals } from './types';
import {
  handleValidDate,
  handleValidTime,
  formatUSDT
} from '../../../helpers/helpers';
import { RootState } from '../../../app/store';
import { statusForNetwork } from '../DirectorInfo/slice';

export const getWithdrawals = createAsyncThunk(
  'withdrawals/fetch',
  async (params: URLSearchParams) => {
    const response = await axiosInstance.get('/mlm/user/balance/withdrawal', {
      params
    });

    const withdrawals = response.data.data.withdrawals.map(
      (withdrawal: any, index: number) => {
        return {
          ...withdrawal,
          checked: false
        };
      }
    );

    const data = {
      count: response.data.data.count,
      withdrawals
    };

    return data;
  }
);

export const getFinupWithdrawals = createAsyncThunk(
  'getFinupWithdrawals/fetch',
  async (params: URLSearchParams) => {
    const response = await axiosInstance.get(
      '/mlm/user/balance/mlp/withdrawal',
      {
        params
      }
    );

    const withdrawals = response.data.data.withdrawals.map(
      (withdrawal: any, index: number) => {
        return {
          ...withdrawal,
          checked: false
        };
      }
    );

    const data = {
      count: response.data.data.count,
      withdrawals
    };

    return data;
  }
);

export const updateAutomaticPayment = createAsyncThunk(
  'withdrawals/updateAutomaticPayment',
  async ({ id }: { id: number }) => {
    return id;
  }
);

const chooseStatusType = (key: WithdrawalStatusEnum) => {
  switch (key) {
    case WithdrawalStatusEnum.CANCELED:
      return 'status/canceled';
    case WithdrawalStatusEnum.PAID:
      return 'status/paid';
  }
};

export const updateWithdrawalStatus = createAsyncThunk(
  'withdrawals/updateWithdrawalStatus',
  async ({ id, type }: { id: number; type: number }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/mlm/user/balance/withdrawal/${chooseStatusType(type)}`,
        {
          id
        }
      );
      toast.success(
        response.data.message || 'Successfully updated withdrawal status'
      );
      return response.data.data as Withdrawals;
    } catch (error: any) {
      toast.error(error.response.data.error);
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const updateFinupWithdrawalStatus = createAsyncThunk(
  'withdrawals/updateFinupWithdrawalStatus',
  async ({ id, type }: { id: number; type: number }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/mlm/user/balance/mlp/withdrawal/${chooseStatusType(type)}`,
        {
          id
        }
      );
      toast.success(
        response.data.message || 'Successfully updated withdrawal status'
      );
      return response.data.data as Withdrawals;
    } catch (error: any) {
      toast.error(error.response.data.error);
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const markAsAutomaticPayout = createAsyncThunk(
  'withdrawals/markAsAutomaticPayout',
  async (ids: number[], { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/mlm/user/balance/withdrawal/payout/automatic`,
        {
          ids
        }
      );

      toast.success(
        response.data.message || 'Successfully updated withdrawal status'
      );
      return response.data.data.withdrawals as Withdrawals[];
    } catch (error: any) {
      toast.error(error.response.data.error);
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const markAsAutomaticFinupPayout = createAsyncThunk(
  'withdrawals/markAsAutomaticFinupPayout',
  async (ids: number[], { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/mlm/user/balance/mlp/withdrawal/payout/automatic`,
        {
          ids
        }
      );

      toast.success(
        response.data.message || 'Successfully updated withdrawal status'
      );
      return response.data.data.withdrawals as Withdrawals[];
    } catch (error: any) {
      toast.error(error.response.data.error);
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const exportWithdrawals = createAsyncThunk(
  'withdrawals/exportWithdrawals',
  async (params: URLSearchParams) => {
    const response = await axiosInstance.get(
      `/mlm/user/balance/withdrawal/export`,
      { params }
    );

    const dataForExport = response.data.data.withdrawals.map(
      (w: Withdrawals) => ({
        id: w.id,
        userId: w.user.id,
        user: `${w.user.name} ${w.user.surname}`,
        walletAddress: w.userWallet?.address || '',
        amount: `${w.paidAmount ? formatUSDT.format(w.paidAmount) : ''}`,
        token: w?.networkCurrencyType,
        network: statusForNetwork(w?.networkType),
        dateCreated: w.dateCreated
          ? `${handleValidDate(w.dateCreated)}, ${handleValidTime(
              w.dateCreated
            )}`
          : '',
        datePaid: w.datePaid
          ? `${handleValidDate(w.datePaid)}, ${handleValidTime(w.datePaid)}`
          : '',
        status: w.status
      })
    );

    return dataForExport;
  }
);

export const exportFinupWithdrawals = createAsyncThunk(
  'withdrawals/exportFinupWithdrawals',
  async (params: URLSearchParams) => {
    const response = await axiosInstance.get(
      `/mlm/user/balance/mlp/withdrawal/export`,
      { params }
    );

    const dataForExport = response.data.data.withdrawals.map(
      (w: Withdrawals) => ({
        id: w.id,
        userId: w.user.id,
        user: `${w.user.name} ${w.user.surname}`,
        walletAddress: w.userWallet?.address || '',
        amount: `${w.paidAmount ? formatUSDT.format(w.paidAmount) : ''}`,
        token: w?.networkCurrencyType,
        network: statusForNetwork(w?.networkType),
        dateCreated: w.dateCreated
          ? `${handleValidDate(w.dateCreated)}, ${handleValidTime(
              w.dateCreated
            )}`
          : '',
        datePaid: w.datePaid
          ? `${handleValidDate(w.datePaid)}, ${handleValidTime(w.datePaid)}`
          : '',
        status: w.status
      })
    );

    return dataForExport;
  }
);

export const setShowActionButtons = createAsyncThunk(
  'withdrawals/setShowActionButtons',
  async (showActionButtons: boolean) => {
    return showActionButtons === true ? false : true;
  }
);

export const exportHeaders = [
  { label: 'ID', key: 'id' },
  { label: 'User ID', key: 'userId' },
  { label: 'User', key: 'user' },
  { label: 'Wallet Address', key: 'walletAddress' },
  { label: 'Network', key: 'network' },
  { label: 'Token', key: 'token' },
  { label: 'Amount', key: 'amount' },
  { label: 'Date Created', key: 'dateCreated' },
  { label: 'Date Paid', key: 'datePaid' },
  { label: 'Status', key: 'status' }
];

export interface WithdrawalsState {
  data: Withdrawals[];
  loading: boolean;
  totalCount: number;
  dataForExport: Withdrawals[];
  loadingExport: boolean;
  showActionButtons: boolean;
}

const initialState = {
  data: [],
  totalCount: 0,
  loading: false,
  dataForExport: [],
  loadingExport: false,
  showActionButtons: false
} as WithdrawalsState;

const withdrawalsSlice = createSlice({
  name: 'withdrawals',
  initialState,
  reducers: {
    removeWithdrawalsForExport(state) {
      state.dataForExport = [];
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getWithdrawals.pending, (state) => {
      state.loading = true;
      state.data = [];
    });
    builder.addCase(getWithdrawals.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.data = payload.withdrawals as Withdrawals[];
      state.totalCount = payload.count;
    });
    builder.addCase(getWithdrawals.rejected, (state) => {
      state.loading = false;
      state.data = [];
    });

    builder.addCase(getFinupWithdrawals.pending, (state) => {
      state.loading = true;
      state.data = [];
    });
    builder.addCase(getFinupWithdrawals.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.data = payload.withdrawals as Withdrawals[];
      state.totalCount = payload.count;
    });
    builder.addCase(getFinupWithdrawals.rejected, (state) => {
      state.loading = false;
      state.data = [];
    });

    builder.addCase(updateAutomaticPayment.fulfilled, (state, { payload }) => {
      const index = state.data.findIndex((p) => p.id === payload);

      state.data[index] = {
        ...state.data[index],
        checked: !state.data[index].checked
      };
    });

    builder.addCase(updateWithdrawalStatus.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateWithdrawalStatus.fulfilled, (state, { payload }) => {
      state.loading = false;
      const index = state.data.findIndex((p) => p.id === payload.id);
      state.data[index] = payload;
    });
    builder.addCase(updateWithdrawalStatus.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateFinupWithdrawalStatus.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      updateFinupWithdrawalStatus.fulfilled,
      (state, { payload }) => {
        state.loading = false;
        const index = state.data.findIndex((p) => p.id === payload.id);
        state.data[index] = payload;
      }
    );
    builder.addCase(updateFinupWithdrawalStatus.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(markAsAutomaticPayout.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(markAsAutomaticPayout.fulfilled, (state, { payload }) => {
      state.loading = false;

      const withdrawalsFromState = state.data;

      const updatedWithdrawals = withdrawalsFromState.map((item1) => {
        const match = payload.find((item2) => item2.id === item1.id);

        return match
          ? { ...item1, ...match, status: match.isPayoutSuccessful ? 5 : 1 }
          : item1;
      });

      state.data = updatedWithdrawals;
    });
    builder.addCase(markAsAutomaticPayout.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(markAsAutomaticFinupPayout.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      markAsAutomaticFinupPayout.fulfilled,
      (state, { payload }) => {
        state.loading = false;

        const withdrawalsFromState = state.data;

        const updatedWithdrawals = withdrawalsFromState.map((item1) => {
          const match = payload.find((item2) => item2.id === item1.id);

          return match
            ? { ...item1, ...match, status: match.isPayoutSuccessful ? 5 : 1 }
            : item1;
        });

        state.data = updatedWithdrawals;
      }
    );
    builder.addCase(markAsAutomaticFinupPayout.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(exportWithdrawals.pending, (state) => {
      state.loadingExport = true;
    });
    builder.addCase(exportWithdrawals.fulfilled, (state, { payload }) => {
      state.loadingExport = false;
      state.dataForExport = payload;
    });
    builder.addCase(exportWithdrawals.rejected, (state) => {
      state.loadingExport = false;
    });

    builder.addCase(exportFinupWithdrawals.pending, (state) => {
      state.loadingExport = true;
    });
    builder.addCase(exportFinupWithdrawals.fulfilled, (state, { payload }) => {
      state.loadingExport = false;
      state.dataForExport = payload;
    });
    builder.addCase(exportFinupWithdrawals.rejected, (state) => {
      state.loadingExport = false;
    });

    builder.addCase(setShowActionButtons.fulfilled, (state, { payload }) => {
      state.showActionButtons = payload;
    });
  }
});

export const { removeWithdrawalsForExport } = withdrawalsSlice.actions;

export default withdrawalsSlice.reducer;
